import svgPaths from "../imports/svg-u9pl9l98rm";
import { motion, type Transition } from "framer-motion";
import type { ReactNode } from "react";
import BurgerArrow from "./BurgerArrow";

interface ClassicBurgerProps {
  isExploded: boolean;
}

const BEIGE = "#F2F1DE";

const enterTransition: Transition = {
  duration: 0.3,
  ease: [0.4, 0, 0.2, 1],
};

const leaveTransition: Transition = {
  type: "spring",
  stiffness: 550,
  damping: 18,
  mass: 1,
};

function getTransition(isExploded: boolean): Transition {
  return isExploded ? enterTransition : leaveTransition;
}

function LabelText({
  lines,
  className = "",
  lineClassNames = [],
}: {
  lines: string[];
  className?: string;
  lineClassNames?: string[];
}) {
  return (
    <div
      className={[
        "flex flex-col justify-center whitespace-nowrap",
        "font-['Diary_Notes',sans-serif] text-[#f2f1de]",
        "text-[8px] leading-[8px] tracking-[0.16px]",
        "min-[380px]:text-[9px] min-[380px]:leading-[9px] min-[380px]:tracking-[0.18px]",
        "sm:text-[10px] sm:leading-[10px] sm:tracking-[0.2px]",
        "lg:text-[12px] lg:leading-[12px] lg:tracking-[0.24px]",
        className,
      ].join(" ")}
    >
      {lines.map((line, index) => (
        <p
          key={`${line}-${index}`}
          className={["mb-0", lineClassNames[index] ?? ""].join(" ")}
        >
          {line}
        </p>
      ))}
    </div>
  );
}
function FlowLabel({
  isExploded,
  side,
  inset = 0,
  children,
}: {
  isExploded: boolean;
  side: "left" | "right";
  inset?: number;
  children?: ReactNode;
}) {
  const explodedX = side === "left" ? inset : -inset;
  const hiddenX = side === "left" ? explodedX - 8 : explodedX + 8;

  return (
    <motion.div
      initial={false}
      className={[
        "pointer-events-none flex shrink-0 overflow-visible",
        side === "left" ? "justify-end" : "justify-start",
      ].join(" ")}
      animate={{
        opacity: isExploded && children ? 1 : 0,
        x: isExploded && children ? explodedX : hiddenX,
      }}
      transition={getTransition(isExploded)}
      aria-hidden={!isExploded}
    >
      <div className="flex items-center gap-2 whitespace-nowrap">
        {children}
      </div>
    </motion.div>
  );
}

function SideLabel({
  isExploded,
  side,
  lines,
  arrowDirection,
  arrowColor,
}: {
  isExploded: boolean;
  side: "left" | "right";
  lines: string[];
  arrowDirection: "left" | "right" | "left-bottom" | "right-top" | "right-bottom";
  arrowColor?: string;
}) {
  return (
    <FlowLabel isExploded={isExploded} side={side} inset={24}>
      {side === "left" ? (
        <>
          <LabelText lines={lines} />
          <BurgerArrow direction={arrowDirection} color={arrowColor} />
        </>
      ) : (
        <>
          <BurgerArrow direction={arrowDirection} color={arrowColor} />
          <LabelText lines={lines} />
        </>
      )}
    </FlowLabel>
  );
}

function DualRightLabels({ isExploded }: { isExploded: boolean }) {
  return (
    <FlowLabel isExploded={isExploded} side="right" inset={24}>
      <div className="flex flex-col items-start gap-2">
        <div className="flex items-center gap-2">
          <BurgerArrow direction="right-top" color={BEIGE} />
          <LabelText lines={["TYBO", "x2"]} />
        </div>

        <div className="flex items-center gap-2">
          <BurgerArrow direction="right-bottom" color={BEIGE} />
          <LabelText lines={["CARNE"]} />
        </div>
      </div>
    </FlowLabel>
  );
}

function LayerRow({
  isExploded,
  y,
  left,
  right,
  children,
}: {
  isExploded: boolean;
  y: number;
  left?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <motion.div
      className="
        mx-auto grid w-fit items-center justify-center gap-x-1
        grid-cols-[68px_133px_68px]
        sm:grid-cols-[80px_133px_80px]
        md:grid-cols-[94px_133px_94px]
      "
      animate={{ y: isExploded ? y : 0 }}
      transition={getTransition(isExploded)}
    >
      <div className="flex h-0 items-center justify-end overflow-visible">
        {left ?? null}
      </div>

      <div className="flex items-center justify-center">
        {children}
      </div>

      <div className="flex h-0 items-center justify-start overflow-visible">
        {right ?? null}
      </div>
    </motion.div>
  );
}

function TopBun() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[36px] rounded-tl-[80px] rounded-tr-[80px] rounded-bl-[8px] rounded-br-[8px] border-4 border-[#f2f1de] sm:h-[40px] md:h-[43.75px]" />
    </div>
  );
}

function LettuceLayer() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="mx-auto h-[8px] w-[100px] sm:w-[112px] md:w-[125px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 129.001 9.88415"
        >
          <path d={svgPaths.p20fb3700} stroke={BEIGE} strokeLinecap="round" strokeWidth="4" />
        </svg>
      </div>
    </div>
  );
}

function CucumberLayer() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="flex items-center gap-[6px] py-[2px] sm:gap-[7px] md:gap-[8px]">
        {[0, 1, 2].map((item) => (
          <div key={item} className="h-[4px] min-w-0 flex-1">
            <svg
              className="block h-full w-full"
              fill="none"
              preserveAspectRatio="none"
              viewBox="0 0 39 4"
            >
              <path d="M0 2H39" stroke={BEIGE} strokeWidth="4" />
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
}

function TomatoLayer() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="flex items-center gap-[8px] p-[2px] sm:gap-[10px] md:gap-[12px]">
        <div className="h-[4px] w-[58px] sm:w-[66px] md:w-[75px]">
          <svg
            className="block h-full w-full"
            fill="none"
            preserveAspectRatio="none"
            viewBox="0 0 79 4"
          >
            <path d="M2 2H77" stroke={BEIGE} strokeLinecap="round" strokeWidth="4" />
          </svg>
        </div>

        <div className="h-[4px] min-w-0 flex-1">
          <svg
            className="block h-full w-full"
            fill="none"
            preserveAspectRatio="none"
            viewBox="0 0 46 4"
          >
            <path d="M2 2H44" stroke={BEIGE} strokeLinecap="round" strokeWidth="4" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function MeatCheeseLayer() {
  return (
    <div className="w-[108px] p-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[16px] sm:h-[18px] md:h-[19px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 133 23"
        >
          <g>
            <path d={svgPaths.p36715d00} fill={BEIGE} />
            <path d={svgPaths.p13a95540} stroke={BEIGE} strokeLinecap="round" strokeWidth="4" />
            <path d={svgPaths.pca7c900} stroke={BEIGE} strokeLinecap="round" strokeWidth="4" />
          </g>
        </svg>
      </div>
    </div>
  );
}

function SauceLayer() {
  return (
    <div className="w-[108px] p-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[8px] sm:h-[8.4px] md:h-[8.875px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 133 11.8701"
        >
          <path d={svgPaths.p326f3cc0} stroke={BEIGE} strokeWidth="4" />
        </svg>
      </div>
    </div>
  );
}

function BottomBun() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[16px] rounded-[8px] border-4 border-[#f2f1de] sm:h-[17px] md:h-[18.75px]" />
    </div>
  );
}

export default function ClassicBurger({ isExploded }: ClassicBurgerProps) {
  const transition = getTransition(isExploded);

  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col items-center justify-center px-2 sm:px-3">
      <div className="flex w-full flex-col items-center gap-0">
        <LayerRow
          isExploded={isExploded}
          y={-40}
          right={
            <SideLabel
              isExploded={isExploded}
              side="right"
              lines={["Pan ", "BRIOCHE"]}
              arrowDirection="right"
              arrowColor="var(--beige-500)"
            />
          }
        >
          <TopBun />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={-30}
          left={
            <SideLabel
              isExploded={isExploded}
              side="left"
              lines={["LECHUGA"]}
              arrowDirection="left"
              arrowColor={BEIGE}
            />
          }
        >
          <LettuceLayer />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={-20}
          right={
            <SideLabel
              isExploded={isExploded}
              side="right"
              lines={["PEPINO"]}
              arrowDirection="right"
              arrowColor={BEIGE}
            />
          }
        >
          <CucumberLayer />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={-10}
          left={
            <SideLabel
              isExploded={isExploded}
              side="left"
              lines={["TOMATE"]}
              arrowDirection="left"
              arrowColor={BEIGE}
            />
          }
        >
          <TomatoLayer />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={0}
          right={<DualRightLabels isExploded={isExploded} />}
        >
          <MeatCheeseLayer />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={10}
          left={
            <SideLabel
              isExploded={isExploded}
              side="left"
              lines={["MAYO", "THOUSAND"]}
              arrowDirection="left-bottom"
              arrowColor={BEIGE}
            />
          }
        >
          <div className="-scale-y-100">
            <SauceLayer />
          </div>
        </LayerRow>

        <motion.div
          className="
            mx-auto grid w-fit items-center justify-center gap-x-1
            grid-cols-[68px_133px_68px]
            sm:grid-cols-[80px_133px_80px]
            md:grid-cols-[94px_133px_94px]
          "
          animate={{ y: isExploded ? 20 : 0 }}
          transition={transition}
        >
          <div />
          <div className="flex justify-center">
            <BottomBun />
          </div>
          <div />
        </motion.div>
      </div>

      <motion.div
        className="mt-4 flex w-full items-center justify-center"
        animate={{ y: isExploded ? 30 : 0 }}
        transition={transition}
      >
        <p className="whitespace-nowrap text-center font-['Roboto_Mono',monospace] text-[28px] font-bold leading-none tracking-[0.06em] text-[#f2f1de] sm:text-[36px] md:text-[44px] lg:text-[48px]">
          CLASSIC
        </p>
      </motion.div>
    </div>
  );
}

export function ClassicBurgerClosed() {
  return <ClassicBurger isExploded={false} />;
}

export function ClassicBurgerExploded() {
  return <ClassicBurger isExploded={true} />;
}